// Create a batch of invitations: who, how many seats, when it expires, send now or later.
import React, { useMemo, useState } from "react";
import { api, ApiError, newIdempotencyKey } from "../../lib/api.js";
import { Notice } from "./ui.jsx";

export function parseList(text) {
  const out = [], bad = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim(); if (!line) continue;
    const m = line.match(/([^\s,<>"']+@[^\s,<>"']+)/);
    if (!m) { bad.push(line); continue; }
    const email = m[1].toLowerCase();
    const name = line.replace(m[1], "").replace(/[<>",\t;]+/g, " ").trim();
    if (name.length < 2) { bad.push(line); continue; }
    out.push({ name, email });
  }
  return { invitees: out, bad };
}

export default function BatchDrawer({ onClose, onDone }) {
  const [kind, setKind] = useState("rep");
  const [text, setText] = useState("");
  const [travellers, setTravellers] = useState(1);
  const [badges, setBadges] = useState(1);
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const idem = useMemo(newIdempotencyKey, [text, travellers, badges, expires, kind]);
  const parsed = useMemo(() => parseList(text), [text]);

  async function create(send) {
    setBusy(true); setError(null);
    try {
      const body = { kind, invitees: parsed.invitees, travellersAllowed: Number(travellers), badgesAllowed: Number(badges), send };
      if (expires) body.expiresAt = new Date(`${expires}T23:59:59`).toISOString();
      const r = await api("/backoffice/invitations", { method: "POST", body, idempotencyKey: idem });
      onDone(`${r.created} created${send ? " and queued to send" : ""}.${r.skipped.length ? ` Skipped ${r.skipped.length}: ` + r.skipped.map(s => `${s.email} (${s.reason})`).join("; ") : ""}`);
    } catch (err) {
      setError(err instanceof ApiError && err.details?.fieldErrors ? Object.values(err.details.fieldErrors).flat()[0] : err.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="bo-drawer-backdrop" onClick={onClose}>
      <aside className="bo-drawer" onClick={e => e.stopPropagation()} aria-label="New batch">
        <div className="bo-drawer-head"><h2>New batch</h2><button className="bo-link" onClick={onClose}>Close</button></div>

        <div className="bo-field-label">Who are they</div>
        <div className="bo-choice-row">
          <button type="button" className={`bo-choice ${kind === "rep" ? "on" : ""}`} onClick={() => setKind("rep")}><b>Lipari employees</b><span>Reps, office staff, national sales. They sign in with their Lipari account.</span></button>
          <button type="button" className={`bo-choice ${kind === "guest" ? "on" : ""}`} onClick={() => setKind("guest")}><b>Guests</b><span>Speakers, consultants, partners. They create a guest account with the invited email.</span></button>
        </div>

        <label className="bo-field">List <span className="help">One per line: name and email, any order. Paste straight from Excel.</span>
          <textarea rows={10} value={text} onChange={e => setText(e.target.value)} placeholder={"Dana Marano, dana@liparifoods.com\nPaul Reyes <paul@liparifoods.com>"} /></label>
        <div className="bo-field-note">{parsed.invitees.length} ready{parsed.bad.length ? ` · ${parsed.bad.length} line${parsed.bad.length === 1 ? "" : "s"} without a usable email` : ""}</div>

        <div className="bo-field-grid">
          <label className="bo-field">Travellers each<input type="number" min={0} max={20} value={travellers} onChange={e => setTravellers(e.target.value)} /></label>
          <label className="bo-field">Badges each<input type="number" min={0} max={50} value={badges} onChange={e => setBadges(e.target.value)} /></label>
          <label className="bo-field">Expires <span className="help">optional</span><input type="date" value={expires} onChange={e => setExpires(e.target.value)} /></label>
        </div>

        {error && <Notice tone="bad">{error}</Notice>}
        <div className="bo-drawer-actions">
          <button className="bo-btn" disabled={busy || !parsed.invitees.length} onClick={() => create(false)}>Create only</button>
          <button className="bo-btn primary" disabled={busy || !parsed.invitees.length} onClick={() => create(true)}>Create and send</button>
        </div>
        <div className="bo-field-note">"Create only" lets you check the list in the table first, then send it in waves.</div>
      </aside>
    </div>
  );
}
